import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { publicSettingsQueryOptions } from "@/lib/settings.queries";
import type { AnalyticsSettings } from "@/lib/settings.functions";
import { Button } from "@/components/ui/button";

/**
 * Site analytics, driven by Settings > Analytics.
 *
 * Mounted once from the root layout. Nothing loads until the public settings
 * name a GA4 measurement ID (and optionally a Plausible domain); with the
 * consent banner on, nothing loads until the visitor accepts. Admin and auth
 * pages are skipped when "Do not track admin pages" is on. gtag.js is
 * injected once per page life and a page_view is sent on every route change.
 *
 * Degrades to nothing: empty settings, a failed settings call, blocked
 * localStorage or a blocked script all leave the page untouched.
 */

const CONSENT_KEY = "mii-consent";
const DECLINE_TTL_MS = 180 * 24 * 60 * 60 * 1000;
const EXCLUDED_PREFIXES = ["/admin", "/auth"];

type Consent = "accepted" | "declined";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function readConsent(): Consent | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { choice?: unknown; at?: unknown };
    if (parsed.choice === "accepted") return "accepted";
    if (parsed.choice === "declined") {
      // A decline is remembered for 180 days, then the bar may ask again.
      if (typeof parsed.at === "number" && Date.now() - parsed.at < DECLINE_TTL_MS)
        return "declined";
      return null;
    }
    return null;
  } catch {
    return null;
  }
}

function writeConsent(choice: Consent) {
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify({ choice, at: Date.now() }));
  } catch {
    // Private mode or blocked storage: the choice lasts for this page life only.
  }
}

/* --------------------------------------------------------------- loaders */

let ga4LoadedFor: string | null = null;
let plausibleLoadedFor: string | null = null;

function ensureGa4(measurementId: string) {
  if (ga4LoadedFor) return;
  ga4LoadedFor = measurementId;
  window.dataLayer = window.dataLayer || [];
  // gtag.js reads the pushed `arguments` objects; a spread array is ignored.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // page_view is sent by hand on route change, so the config call must not send one.
  window.gtag("config", measurementId, { send_page_view: false });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(s);
}

function ensurePlausible(domain: string) {
  if (plausibleLoadedFor) return;
  plausibleLoadedFor = domain;
  // Plausible's script tracks SPA navigations itself (it hooks pushState).
  const s = document.createElement("script");
  s.defer = true;
  s.dataset.domain = domain;
  s.src = "https://plausible.io/js/script.js";
  document.head.appendChild(s);
}

function isExcluded(pathname: string): boolean {
  return EXCLUDED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/* ------------------------------------------------------------- component */

export function Analytics() {
  const { data } = useQuery(publicSettingsQueryOptions());
  const analytics: AnalyticsSettings | undefined = data?.analytics;
  const router = useRouter();

  const [pathname, setPathname] = useState(() => router.state.location.pathname);
  const [consent, setConsent] = useState<Consent | null>(() =>
    typeof window === "undefined" ? null : readConsent(),
  );

  // Follow route changes (TanStack Router; fires after each navigation settles).
  useEffect(() => {
    return router.subscribe("onResolved", (ev) => {
      setPathname(ev.toLocation.pathname);
    });
  }, [router]);

  const ga4 = analytics?.ga4MeasurementId.trim() ?? "";
  const plausible = analytics?.plausibleDomain.trim() ?? "";
  const configured = ga4 !== "" || plausible !== "";
  const excluded = !!analytics?.excludeAdmin && isExcluded(pathname);
  const needsConsent = !!analytics?.consentBannerEnabled;
  const allowed = configured && !excluded && (!needsConsent || consent === "accepted");

  // Load the scripts (once) and send a page_view for every path we land on.
  useEffect(() => {
    if (!allowed) return;
    if (plausible) ensurePlausible(plausible);
    if (ga4) {
      ensureGa4(ga4);
      window.gtag?.("event", "page_view", {
        page_path: pathname + router.state.location.searchStr,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    // `router` is stable; the search string is read at send time on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, ga4, plausible, pathname]);

  const showBar = configured && needsConsent && consent === null && !excluded;
  if (!showBar) return null;

  const choose = (choice: Consent) => {
    writeConsent(choice);
    setConsent(choice);
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 text-card-foreground shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <p className="text-sm leading-snug text-muted-foreground">
          {analytics?.consentText.trim() || "We use cookies to measure site traffic."}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => choose("declined")}>
            Decline
          </Button>
          <Button type="button" size="sm" onClick={() => choose("accepted")}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
